<script lang="ts">
  // Galaxy map v3 (SVG): proper star glyphs with glow, explored/unexplored fog,
  // fuel-range shading, in-flight fleet markers with travel progress, monster
  // lairs vs Andromedan raids, blockade badges, move ordering with re-routing.
  import { selectors, inRange, isBlockaded, fuelRangeCp, supportStars, areAtWar, shortEntityId } from '@engine/index';
  import { buildableItems, itemLabel } from '@engine/items';
  import { shipMarines } from '@engine/economy';
  import type { StarColor } from '@engine/types';
  import { MAP_SIZE } from '@engine/galaxy';
  import { playerColor, STAR_COLORS } from '../colors';
  import PixelPlanet from '../PixelPlanet.svelte';
  import { generateTerrain, TERRAIN_INFO, TERRAIN_W, TERRAIN_H, DEFENSE_TACTICS } from '@engine/groundTactics';
  import { app, getActive, savePerGame } from '../state.svelte';
  import { BUILD_HOTKEYS, bestColonyFor, cancelPin, pinBuild, pinnedStatus, resolveHotkeyItem } from '../quickBuild';
  import AutopilotBar from '../components/AutopilotBar.svelte';
  import PlanetDialog from '../components/PlanetDialog.svelte';

  const MAP_BG_CACHE = new Map<string, string>();

  function hashText(s: string): number {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  }

  function mulberry32(seed: number): () => number {
    let t = seed >>> 0;
    return () => {
      t = (t + 0x6d2b79f5) >>> 0;
      let r = Math.imul(t ^ (t >>> 15), 1 | t);
      r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
      return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
    };
  }

  /** Colorful pixel-art galaxy backdrop (bugs.md: like bugs/galaxy_compact.png).
   * Two layers: nebulae/arms are drawn at 1/PX resolution for the chunky
   * pixel-cluster look, then composited onto a FULL-res canvas where every
   * background star is a true single pixel (round-4 feedback: 5px star squares
   * competed with the real star glyphs). */
  function makeGalaxyBackground(
    seedText: string,
    w: number,
    h: number,
    /** real star positions in TEXTURE coordinates — the weather forms around
     * them, so bright bands and nebulae sit where the stars actually are */
    starPts: Array<{ x: number; y: number }> = [],
  ): string {
    const PX = 5; // art-pixel size of the nebula layer — bigger = chunkier
    const seed = hashText(seedText);
    const rnd = mulberry32(seed);
    const lw = Math.ceil(w / PX);
    const lh = Math.ceil(h / PX);
    const canvas = document.createElement('canvas');
    canvas.width = lw;
    canvas.height = lh;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';
    /** star positions collected during the lo-res pass, drawn 1px at full res */
    const flecks: Array<{ x: number; y: number; warm: boolean; a: number }> = [];

    // deep-space base + bright warm core (the reference galaxy glows white-blue)
    ctx.fillStyle = '#05060f';
    ctx.fillRect(0, 0, lw, lh);
    const cx = lw * (0.46 + rnd() * 0.08);
    const cy = lh * (0.46 + rnd() * 0.08);
    const blob = (x: number, y: number, r: number, rgba: string) => {
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, rgba);
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.fillRect(x - r, y - r, r * 2, r * 2);
    };
    blob(cx, cy, Math.max(lw, lh) * 0.5, 'rgba(96,112,190,0.34)');
    blob(cx, cy, Math.min(lw, lh) * 0.18, 'rgba(240,236,220,0.6)');
    blob(cx, cy, Math.min(lw, lh) * 0.08, 'rgba(255,250,235,0.9)');

    // spiral arms: saturated blue-lavender haze + bright star flecks along them
    const armCount = 3;
    const armTurns = 2.1 + rnd() * 0.7;
    const maxR = Math.min(lw, lh) * (0.5 + rnd() * 0.08);
    const squish = 0.72 + rnd() * 0.16;
    for (let arm = 0; arm < armCount; arm++) {
      const armPhase = (Math.PI * 2 * arm) / armCount + rnd() * 0.25;
      for (let i = 0; i < 190; i++) {
        const t = i / 190;
        const th = armPhase + t * Math.PI * armTurns;
        const r = t * maxR + (rnd() - 0.5) * 9;
        const x = cx + Math.cos(th) * r;
        const y = cy + Math.sin(th) * r * squish;
        const rr = 3 + rnd() * 9;
        blob(x, y, rr, `rgba(${110 + Math.floor(rnd() * 50)},${120 + Math.floor(rnd() * 50)},${190 + Math.floor(rnd() * 60)},${0.12 + rnd() * 0.12})`);
        // arm star flecks render 1px at FULL res — collect positions here
        if (rnd() < 0.5) {
          flecks.push({
            x: (x + (rnd() - 0.5) * 6) * PX,
            y: (y + (rnd() - 0.5) * 6) * PX,
            warm: rnd() < 0.25,
            a: 0.35 + rnd() * 0.45,
          });
        }
      }
    }

    // star-cluster haze: every REAL star seeds its own pocket of luminous gas,
    // so the galaxy's bright structure traces the actual constellation the
    // player will fly (bugs.md round 5: "match the chosen visiting stars")
    const lp = starPts.map((p) => ({ x: p.x / PX, y: p.y / PX }));
    for (const p of lp) {
      for (let k = 0; k < 3; k++) {
        const rr = 3 + rnd() * 6;
        blob(
          p.x + (rnd() - 0.5) * 9,
          p.y + (rnd() - 0.5) * 9,
          rr,
          `rgba(${118 + Math.floor(rnd() * 40)},${128 + Math.floor(rnd() * 40)},${196 + Math.floor(rnd() * 50)},${0.1 + rnd() * 0.08})`,
        );
      }
      blob(p.x, p.y, 2.5 + rnd() * 2, `rgba(235,230,214,${0.12 + rnd() * 0.08})`);
    }

    // nebulae: the reference's loud color patches — green, violet, magenta,
    // ember and teal clusters of overlapping chunky blobs, anchored on real
    // stars when we know them so the weather has somewhere to be visited
    const nebColors = [
      [72, 200, 118],
      [134, 82, 224],
      [214, 72, 134],
      [224, 96, 58],
      [62, 182, 192],
    ] as const;
    const order = [...nebColors].sort(() => rnd() - 0.5);
    const anchors = [...lp].sort(() => rnd() - 0.5);
    const nebulaCount = 4 + Math.floor(rnd() * 2);
    for (let n = 0; n < nebulaCount; n++) {
      const anchor = anchors[n];
      const px = anchor ? anchor.x + (rnd() - 0.5) * lw * 0.06 : lw * (0.1 + rnd() * 0.8);
      const py = anchor ? anchor.y + (rnd() - 0.5) * lh * 0.06 : lh * (0.1 + rnd() * 0.8);
      const [cr, cg, cb] = order[n % order.length]!;
      for (let k = 0; k < 16; k++) {
        const ox = (rnd() - 0.5) * (lw * 0.18);
        const oy = (rnd() - 0.5) * (lh * 0.14);
        const rr = 5 + rnd() * (Math.min(lw, lh) * 0.09);
        blob(px + ox, py + oy, rr, `rgba(${cr},${cg},${cb},${0.10 + rnd() * 0.14})`);
      }
      // a few lit knots inside each nebula — also 1px at full res
      for (let k = 0; k < 6; k++) {
        flecks.push({
          x: (px + (rnd() - 0.5) * lw * 0.12) * PX,
          y: (py + (rnd() - 0.5) * lh * 0.1) * PX,
          warm: false,
          a: 0.3 + rnd() * 0.3,
        });
      }
    }

    // gas stipple: per-pixel brightness jitter turns the smooth blob gradients
    // into dithered pixel clouds — the "universe weather" of the reference art.
    // Every art-pixel of nebula/arm haze gets its own random luminance, so the
    // clouds read as grainy colored gas instead of airbrush.
    const img = ctx.getImageData(0, 0, lw, lh);
    const d = img.data;
    for (let i = 0; i < d.length; i += 4) {
      const j = rnd() < 0.12 ? 0.45 + rnd() * 0.3 : 0.74 + rnd() * 0.52;
      d[i] = Math.min(255, d[i]! * j);
      d[i + 1] = Math.min(255, d[i + 1]! * j);
      d[i + 2] = Math.min(255, d[i + 2]! * j);
    }
    ctx.putImageData(img, 0, 0);

    // ---- full-res composite: chunky nebula layer under single-pixel stars ----
    const full = document.createElement('canvas');
    full.width = w;
    full.height = h;
    const fx = full.getContext('2d');
    if (!fx) return canvas.toDataURL('image/png');
    fx.imageSmoothingEnabled = false; // keep the nebula pixels hard-edged
    fx.drawImage(canvas, 0, 0, w, h);

    for (const f of flecks) {
      fx.fillStyle = f.warm ? `rgba(255,244,214,${f.a})` : `rgba(214,228,255,${f.a})`;
      fx.fillRect(Math.floor(f.x), Math.floor(f.y), 1, 1);
    }
    // field sprinkle — true 1px stars over the whole sky
    const starCount = Math.floor((w * h) / 1100);
    for (let i = 0; i < starCount; i++) {
      const x = Math.floor(rnd() * w);
      const y = Math.floor(rnd() * h);
      const b = rnd();
      const alpha = 0.2 + b * 0.55;
      const tint = rnd();
      if (tint < 0.12) fx.fillStyle = `rgba(255,225,190,${alpha})`;
      else if (tint > 0.9) fx.fillStyle = `rgba(190,215,255,${alpha})`;
      else fx.fillStyle = `rgba(240,246,255,${alpha})`;
      fx.fillRect(x, y, 1, 1);
    }

    // vignette last so the star field dims toward the edges too
    const vg = fx.createRadialGradient(w * 0.5, h * 0.5, Math.min(w, h) * 0.3, w * 0.5, h * 0.5, Math.max(w, h) * 0.82);
    vg.addColorStop(0, 'rgba(0,0,0,0)');
    vg.addColorStop(1, 'rgba(2,3,8,0.55)');
    fx.fillStyle = vg;
    fx.fillRect(0, 0, w, h);

    return full.toDataURL('image/png');
  }

  // replay mode (0.32.0): a saved game's reconstructed state + a chosen
  // perspective replace the live session. All order-issuing UI is inert.
  const {
    replayState = null,
    replayViewAs = null,
  }: { replayState?: import('@engine/types').GameState | null; replayViewAs?: number | null } = $props();
  const replay = $derived(replayState !== null);
  const session = () => getActive()!.session;
  const gs = $derived.by(() => {
    if (replayState) return replayState;
    void app.version;
    return session().getPlanned();
  });
  const me = () => (replayState ? (replayViewAs ?? 0) : session().playerId);
  /** reconciliation: the economy follows the recorded script — every build
   * affordance on the map (quick-build, B hotkeys, governor bar) goes dark */
  const scripted = $derived(gs?.reconcile !== undefined);
  const foeName = (id: number) => gs?.empires.find((e) => e.id === id)?.raceName ?? `#${id}`;
  const view = $derived.by(() => (gs ? selectors.galaxyView(gs, me()) : []));
  const fleets = $derived.by(() => (gs ? selectors.fleetRows(gs, me()) : []));
  const reachable = $derived.by(() => {
    if (!gs) return new Set<number>();
    const out = new Set<number>();
    for (const star of gs.stars) {
      if (inRange(gs, me(), star)) out.add(star.id);
    }
    return out;
  });
  const monstersByStar = $derived.by(() => {
    const m = new Map<number, string[]>();
    if (!gs) return m;
    for (const mon of gs.monsters) {
      m.set(mon.starId, [...(m.get(mon.starId) ?? []), mon.kind]);
    }
    return m;
  });
  const isRaid = (kinds: string[] | undefined) => (kinds ?? []).some((k) => k.startsWith('antaran_'));
  const blockadedStars = $derived.by(() => {
    const out = new Set<number>();
    if (!gs) return out;
    for (const c of gs.colonies) {
      if (c.owner !== me() || c.outpost) continue;
      if (isBlockaded(gs, c)) {
        const p = gs.planets.find((x) => x.id === c.planetId);
        if (p) out.add(p.starId);
      }
    }
    return out;
  });
  /** fuel-range envelope: circles around every colony/outpost star */
  let showRange = $state(true);

  /** map zoom mode: 'fit' shows the whole galaxy, 'zoom' is a scrollable
   * close-up. Persisted so a reload keeps the chosen view (bugs.md). */
  let mapZoom = $state<'fit' | 'zoom'>(
    typeof localStorage !== 'undefined' && localStorage.getItem('moo2.mapZoom') === 'zoom' ? 'zoom' : 'fit',
  );
  const MIN_SCALE = 0.2;
  const MAX_SCALE = 2.4;
  const clampScale = (s: number) => Math.max(MIN_SCALE, Math.min(MAX_SCALE, s));
  const clampFitScale = (s: number) => Math.min(MAX_SCALE, s);
  let mapScale = $state<number>((() => {
    if (typeof localStorage === 'undefined') return 0.85;
    const raw = Number(localStorage.getItem('moo2.mapScale') ?? '0.85');
    return Number.isFinite(raw) ? clampScale(raw) : 0.85;
  })());
  let fitScale = $state(1);
  const mapDims = $derived(gs ? MAP_SIZE[gs.settings.galaxySize] : { w: 2000, h: 1500 });
  const renderScale = $derived(mapZoom === 'fit' ? fitScale : mapScale);
  let mapBgUrl = $state('');
  const mapSvgStyle = $derived.by(() => {
    const base = `width:${Math.round(mapDims.w * renderScale)}px;max-width:none`;
    if (!mapBgUrl) return base;
    // pixelated: the backdrop is authored at 1/5 res — upscaling must keep the
    // chunky art-pixels instead of smoothing them away
    return `${base};background-image:url(${mapBgUrl});background-size:cover;background-position:center;image-rendering:pixelated`;
  });

  function recomputeFitScale() {
    if (!mapScroller) return;
    const w = Math.max(1, mapScroller.clientWidth);
    const h = Math.max(1, mapScroller.clientHeight);
    fitScale = clampFitScale(Math.min(w / mapDims.w, h / mapDims.h));
  }

  function persistMapZoom() {
    try {
      localStorage.setItem('moo2.mapZoom', mapZoom);
      localStorage.setItem('moo2.mapScale', String(mapScale));
    } catch {
      // private mode: zoom still works for this session
    }
  }

  function toggleMapZoom() {
    if (mapZoom === 'fit') {
      mapZoom = 'zoom';
      // start zoom mode exactly from fit scale (no visible jump)
      mapScale = fitScale;
    } else {
      mapZoom = 'fit';
    }
    persistMapZoom();
  }

  function onMapWheel(ev: WheelEvent) {
    // Keep browser/page zoom intact globally; only intercept Ctrl/Cmd wheel
    // while hovering the galaxy map and apply it to map zoom instead.
    if (!ev.ctrlKey && !ev.metaKey) return;
    ev.preventDefault();
    const dir = ev.deltaY < 0 ? 1 : -1;
    const step = ev.shiftKey ? 0.16 : 0.08;

    let focusX = 0;
    let focusY = 0;
    let contentX = 0;
    let contentY = 0;
    if (mapScroller) {
      const rect = mapScroller.getBoundingClientRect();
      focusX = Math.max(0, Math.min(mapScroller.clientWidth, ev.clientX - rect.left));
      focusY = Math.max(0, Math.min(mapScroller.clientHeight, ev.clientY - rect.top));
      contentX = mapScroller.scrollLeft + focusX;
      contentY = mapScroller.scrollTop + focusY;
    }

    let oldScale = mapZoom === 'fit' ? fitScale : mapScale;
    if (mapZoom === 'fit') {
      // wheel-in from fit enters zoom mode seamlessly at the same scale
      if (dir < 0) return;
      mapZoom = 'zoom';
      mapScale = fitScale;
      oldScale = fitScale;
    }

    const nextScale = clampScale(oldScale + dir * step);

    // Unified end-state: zooming out to the fit scale snaps to fit mode.
    if (dir < 0 && nextScale <= fitScale + 0.0005) {
      mapScale = fitScale;
      mapZoom = 'fit';
      persistMapZoom();
      return;
    }

    if (nextScale === oldScale) {
      persistMapZoom();
      return;
    }

    mapScale = nextScale;
    persistMapZoom();

    if (mapScroller) {
      const ratio = nextScale / oldScale;
      requestAnimationFrame(() => {
        if (!mapScroller) return;
        const maxLeft = Math.max(0, mapScroller.scrollWidth - mapScroller.clientWidth);
        const maxTop = Math.max(0, mapScroller.scrollHeight - mapScroller.clientHeight);
        const left = contentX * ratio - focusX;
        const top = contentY * ratio - focusY;
        mapScroller.scrollLeft = Math.max(0, Math.min(maxLeft, left));
        mapScroller.scrollTop = Math.max(0, Math.min(maxTop, top));
      });
    }
  }

  let mapScroller = $state<HTMLDivElement | null>(null);
  let panning = $state(false);
  let panPointerId = -1;
  let panCandidate = false;
  let panStartX = 0;
  let panStartY = 0;
  let panStartLeft = 0;
  let panStartTop = 0;
  let suppressClickUntil = 0;

  function onMapPointerDown(ev: PointerEvent) {
    if (mapZoom !== 'zoom' || ev.button !== 0 || !mapScroller) return;
    panCandidate = true;
    panning = false;
    panPointerId = ev.pointerId;
    panStartX = ev.clientX;
    panStartY = ev.clientY;
    panStartLeft = mapScroller.scrollLeft;
    panStartTop = mapScroller.scrollTop;
  }

  function onMapPointerMove(ev: PointerEvent) {
    if (ev.pointerId !== panPointerId || !panCandidate || !mapScroller) return;
    const dx = ev.clientX - panStartX;
    const dy = ev.clientY - panStartY;
    if (!panning) {
      if (Math.abs(dx) + Math.abs(dy) <= 4) return;
      panning = true;
      suppressClickUntil = Date.now() + 140;
      mapScroller.setPointerCapture(ev.pointerId);
      ev.preventDefault();
    }
    mapScroller.scrollLeft = panStartLeft - dx;
    mapScroller.scrollTop = panStartTop - dy;
  }

  function endMapPan(ev?: PointerEvent) {
    if (!mapScroller || (ev && ev.pointerId !== panPointerId)) return;
    if (ev && mapScroller.hasPointerCapture(ev.pointerId)) {
      mapScroller.releasePointerCapture(ev.pointerId);
    }
    panCandidate = false;
    panning = false;
    panPointerId = -1;
  }
  $effect(() => {
    void mapDims.w;
    void mapDims.h;
    requestAnimationFrame(() => recomputeFitScale());
  });
  $effect(() => {
    if (!mapScroller) return;
    if (typeof ResizeObserver === 'undefined') {
      requestAnimationFrame(() => recomputeFitScale());
      return;
    }
    const ro = new ResizeObserver(() => recomputeFitScale());
    ro.observe(mapScroller);
    requestAnimationFrame(() => recomputeFitScale());
    return () => ro.disconnect();
  });
  $effect(() => {
    if (!gs || typeof document === 'undefined') {
      mapBgUrl = '';
      return;
    }
    // one shared scale factor keeps the texture's aspect IDENTICAL to the
    // map's, so background-size:cover is an exact fit and star positions map
    // linearly onto the weather painted around them
    let f = Math.max(0.55, 900 / mapDims.w, 700 / mapDims.h);
    f = Math.min(f, 2048 / mapDims.w, 1536 / mapDims.h);
    const texW = Math.round(mapDims.w * f);
    const texH = Math.round(mapDims.h * f);
    const key = `${JSON.stringify(gs.seed)}:${mapDims.w}x${mapDims.h}:${texW}x${texH}`;
    const cached = MAP_BG_CACHE.get(key);
    if (cached) {
      mapBgUrl = cached;
      return;
    }
    const pts = gs.stars.map((s) => ({ x: s.x * f, y: s.y * f }));
    const url = makeGalaxyBackground(key, texW, texH, pts);
    if (url) {
      MAP_BG_CACHE.set(key, url);
      mapBgUrl = url;
    }
  });
  const rangeCircles = $derived.by(() => {
    if (!gs || !showRange) return [];
    const empire = gs.empires.find((e) => e.id === me());
    if (!empire) return [];
    const r = Math.min(fuelRangeCp(empire), 4000);
    return supportStars(gs, me()).map((s) => ({ x: s.x, y: s.y, r }));
  });

  let selectedStarId = $state<number | null>(null);
  let selectedShipIds = $state<number[]>([]);
  // colony-ship arrival alert: "View on map" hands us the star to select
  $effect(() => {
    if (app.focusStarId !== null) {
      selectedStarId = app.focusStarId;
      app.focusStarId = null;
    }
  });
  // the fleets explainer shows until dismissed once (then never again)
  let showFleetTip = $state(localStorage.getItem('moo2.tip.fleets') !== '0');
  function dismissFleetTip() {
    showFleetTip = false;
    localStorage.setItem('moo2.tip.fleets', '0');
  }

  const selected = $derived(view.find((v) => v.star.id === selectedStarId) ?? null);
  const shipsHere = $derived(fleets.filter((f) => f.atStarId === selectedStarId));
  /** wormhole partner of the selected star: always a valid move target —
   * but only shown once the wormhole itself is known (visited/scanned) */
  const wormholeTarget = $derived.by(() => {
    if (!gs || selectedStarId === null) return null;
    const sv = view.find((v) => v.star.id === selectedStarId);
    return sv?.wormholeVisible ? sv.star.wormholeTo : null;
  });

  /** own fleets in flight (or ordered this turn): marker at progress point */
  const transits = $derived.by(() => {
    if (!gs) return [];
    const starAt = (id: number) => gs.stars.find((s) => s.id === id);
    const out: Array<{
      id: number;
      name: string;
      x: number;
      y: number;
      tx: number;
      ty: number;
      angle: number;
      eta: number;
      reroutable: boolean;
    }> = [];
    let lane = 0;
    for (const f of fleets) {
      if (!f.transit) continue;
      const from = starAt(f.transit.fromStarId);
      const to = starAt(f.transit.toStarId);
      if (!from || !to) continue;
      const total = Math.max(1, f.transit.arrivalTurn - f.transit.departedTurn);
      const p = Math.max(0.06, Math.min(0.94, (gs.turn - f.transit.departedTurn) / total));
      // spread overlapping fleets on the same lane a little
      const off = (lane++ % 3) * 14 - 14;
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const len = Math.max(1, Math.hypot(dx, dy));
      const nx = -dy / len;
      const ny = dx / len;
      out.push({
        id: f.ship.id,
        name: f.name,
        x: from.x + dx * p + nx * off,
        y: from.y + dy * p + ny * off,
        tx: to.x,
        ty: to.y,
        angle: (Math.atan2(dy, dx) * 180) / Math.PI,
        eta: Math.max(1, f.transit.arrivalTurn - gs.turn),
        reroutable: f.reroutable,
      });
    }
    return out;
  });

  /** enemy empires with ships at the selected star that we are NOT at war with */
  const peacefulForeigners = $derived.by(() => {
    if (!gs || !selected) return [];
    const owners = [...new Set(selected.ships.map((s) => s.owner))].filter((o) => o !== me() && o >= 0);
    return owners
      .filter((o) => !areAtWar(gs, me(), o))
      .map((o) => gs.empires.find((e) => e.id === o)?.name ?? `#${o}`);
  });

  /** transient move/settle feedback: silent failures read as a dead UI */
  let mapNote = $state('');
  let mapNoteTimer: ReturnType<typeof setTimeout> | null = null;
  function showNote(text: string) {
    mapNote = text;
    if (mapNoteTimer) clearTimeout(mapNoteTimer);
    mapNoteTimer = setTimeout(() => (mapNote = ''), 6000);
  }

  function clickStar(starId: number) {
    if (Date.now() < suppressClickUntil) return;
    if (!replay && selectedShipIds.length > 0 && selectedStarId !== starId) {
      // prune ships that no longer exist (destroyed in a battle) so a stale
      // selection can't wedge star selection with endless invalid moves
      const live = new Set(fleets.map((f) => f.ship.id));
      selectedShipIds = selectedShipIds.filter((id) => live.has(id));
      if (selectedShipIds.length > 0) {
        const res = session().submit('move_ships', { shipIds: selectedShipIds, destStarId: starId });
        if (!res.error) selectedShipIds = [];
        else showNote(`⛔ ${res.error}`);
        return;
      }
    }
    selectedStarId = starId;
  }

  function toggleShip(id: number) {
    selectedShipIds = selectedShipIds.includes(id)
      ? selectedShipIds.filter((x) => x !== id)
      : [...selectedShipIds, id];
  }

  // ---- fleet-selection hotkeys (bugs.md: ordering ships around was a
  // checkmark game). A = select all / none at the selected star; Backspace
  // cycles ship1 -> ship2 -> ... -> all, for peeling single ships off fast.
  function selectAllShips() {
    const ids = shipsHere.map((f) => f.ship.id);
    const allIn = ids.length > 0 && ids.every((id) => selectedShipIds.includes(id));
    selectedShipIds = allIn ? [] : ids;
  }
  function cycleShipSelection() {
    const ids = shipsHere.map((f) => f.ship.id);
    if (!ids.length) return;
    const idx = selectedShipIds.length === 1 ? ids.indexOf(selectedShipIds[0]!) : -1;
    if (idx >= 0 && idx < ids.length - 1) selectedShipIds = [ids[idx + 1]!];
    else if (idx === ids.length - 1) selectedShipIds = [...ids];
    else selectedShipIds = [ids[0]!];
  }
  function onMapKey(e: KeyboardEvent) {
    if (replay) return;
    const t = e.target as HTMLElement | null;
    const typing = !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
    if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
    const key = e.key.toLowerCase();
    if (e.key === 'Escape') {
      if (buildArm) {
        e.preventDefault();
        buildArm = false;
      }
      return;
    }
    // build mode: B arms the selected star, then one key per item queues it
    // at the best-suited colony there (pinned — autopilot waits for it)
    if (buildArm && selectedStarId !== null && BUILD_HOTKEYS[key]) {
      e.preventDefault();
      queueHotkey(key);
      return;
    }
    if (key === 'b' && selectedStarId !== null && myColonyHere && !scripted) {
      e.preventDefault();
      buildArm = true;
      return;
    }
    if (!shipsHere.length) return;
    if (key === 'a') {
      e.preventDefault();
      selectAllShips();
    } else if (e.key === 'Backspace') {
      e.preventDefault();
      cycleShipSelection();
    } else if (key === 'c') {
      e.preventDefault();
      hotSettle('colonize');
    } else if (key === 'o') {
      e.preventDefault();
      hotSettle('outpost');
    } else if (key === 'l') {
      e.preventDefault();
      hotTransports('load');
    } else if (key === 'u') {
      e.preventDefault();
      hotTransports('unload');
    }
  }

  function colonize(shipId: number, planetId: number) {
    if (replay) return;
    const res = session().submit('colonize', { shipId, planetId });
    if (res.error) showNote(`⛔ ${res.error}`);
  }
  function outpost(shipId: number, planetId: number) {
    if (replay) return;
    const res = session().submit('build_outpost', { shipId, planetId });
    if (res.error) showNote(`⛔ ${res.error}`);
  }

  // ---- fleet hotkeys: C colonizes / O outposts the best open planet here
  // with a suitable ship; L / U load and unload a transport at the colony ----
  function hotSettle(kind: 'colonize' | 'outpost') {
    const pool = selectedShipIds.length ? shipsHere.filter((f) => selectedShipIds.includes(f.ship.id)) : shipsHere;
    const able = (f: (typeof shipsHere)[number]) => (kind === 'colonize' ? f.canColonizeHere : f.canOutpostHere);
    const f = pool.find((x) => able(x).length) ?? shipsHere.find((x) => able(x).length);
    if (!f || !gs) {
      showNote(`⛔ no ship here can ${kind === 'colonize' ? 'colonize' : 'build an outpost on'} a planet in this system`);
      return;
    }
    // biggest world first — the closest thing to "best" without a dialog
    const planets = able(f)
      .map((id) => gs.planets.find((p) => p.id === id))
      .filter((p) => p !== undefined)
      .sort((a, b) => b.sizeClass - a.sizeClass || a.id - b.id);
    const target = planets[0];
    if (!target) return;
    if (kind === 'colonize') colonize(f.ship.id, target.id);
    else outpost(f.ship.id, target.id);
  }
  function hotTransports(kind: 'load' | 'unload') {
    if (replay) return;
    const pool = selectedShipIds.length ? shipsHere.filter((f) => selectedShipIds.includes(f.ship.id)) : shipsHere;
    const colonyOf = (f: (typeof shipsHere)[number]) =>
      kind === 'load' ? f.canLoadFromColonyId : f.canUnloadToColonyId;
    const f = pool.find((x) => colonyOf(x) !== null) ?? shipsHere.find((x) => colonyOf(x) !== null);
    if (!f) {
      showNote(`⛔ no transport here can ${kind} colonists at this star`);
      return;
    }
    const res = session().submit(kind === 'load' ? 'load_transports' : 'unload_transports', {
      colonyId: colonyOf(f),
      shipId: f.ship.id,
    });
    if (res.error) showNote(`⛔ ${res.error}`);
    else if (kind !== 'load') showNote('🚛 colonists unloaded');
    else {
      // at war the same L keystroke is the invasion on-ramp — say so
      const warOn = gs?.empires.some((e) => e.id !== me() && !e.eliminated && areAtWar(gs!, me(), e.id));
      showNote(warOn ? '🪖 2 marines aboard — drop them on an enemy colony with a clear sky' : '🚛 2 colonists aboard');
    }
  }

  // ---- map-view quick builds (pinned): B arms the selected star, item keys
  // queue at the best-suited colony, the status panel tracks completion ----
  let buildArm = $state(false);
  $effect(() => {
    void selectedStarId;
    buildArm = false; // a new selection always starts un-armed
  });
  const myColonyHere = $derived.by(() => {
    if (!gs || selectedStarId === null) return false;
    return gs.colonies.some((c) => {
      if (c.owner !== me() || c.outpost) return false;
      const p = gs.planets.find((x) => x.id === c.planetId);
      return p?.starId === selectedStarId;
    });
  });
  /** strongest yard at the selected star: names the build-mode hint and
   * feeds the non-hotkey dropdown its buildable list */
  const bestYardHere = $derived.by(() => {
    if (!gs || selectedStarId === null || !myColonyHere) return null;
    const mine = gs.colonies.filter((c) => {
      if (c.owner !== me() || c.outpost) return false;
      const p = gs.planets.find((x) => x.id === c.planetId);
      return p?.starId === selectedStarId;
    });
    let best: { colony: (typeof mine)[number]; prod: number } | null = null;
    for (const colony of mine) {
      const prod = selectors.colonyRow(gs, colony).output.prodToQueue;
      if (!best || prod > best.prod) best = { colony, prod };
    }
    return best?.colony ?? null;
  });
  function queueHotkey(key: string) {
    if (!gs || selectedStarId === null) return;
    const spec = BUILD_HOTKEYS[key]!;
    const resolved = resolveHotkeyItem(gs, me(), key);
    if ('error' in resolved) {
      showNote(`⛔ ${resolved.error}`);
      return;
    }
    queuePinnedItem(resolved.item, spec.label, resolved.note);
  }
  function queuePinnedItem(item: string, label: string, note?: string) {
    if (!gs || selectedStarId === null) return;
    const best = bestColonyFor(gs, me(), selectedStarId, item);
    if ('error' in best) {
      showNote(`⛔ ${best.error}`);
      return;
    }
    const res = pinBuild(session(), app.pins, best.colony, item);
    if (res.error) {
      showNote(`⛔ ${res.error}`);
      return;
    }
    savePerGame();
    showNote(`🔨 ${label} queued at ${best.colony.name}${note ? ` — ${note}` : ''}`);
  }
  /** dropdown alternative to the hotkeys (same pinning semantics) */
  const buildOptions = $derived.by(() => {
    if (!gs || !bestYardHere) return [];
    return buildableItems(gs, bestYardHere)
      .filter((id) => id !== 'trade_goods' && !id.startsWith('refit:'))
      .map((id) => ({ id, label: itemLabel(gs, me(), id) }));
  });
  const pinStatuses = $derived.by(() => {
    void app.version;
    void app.pins;
    if (!gs) return [];
    // whole hand-edited queues are pins now (ui_streamlining.md P4), so show
    // one bar per colony — the item actually building — with a queue count,
    // instead of a bar per pinned item flooding the strip
    const all = pinnedStatus(gs, me(), app.pins);
    const byColony = new Map<number, (typeof all)[number] & { more: number }>();
    for (const ps of all) {
      const head = byColony.get(ps.colonyId);
      if (!head || ps.queuePos < head.queuePos) byColony.set(ps.colonyId, { ...ps, more: (head?.more ?? 0) + (head ? 1 : 0) });
      else head.more++;
    }
    return [...byColony.values()];
  });
  function cancelPinnedBuild(colonyId: number, pinIndex: number) {
    if (replay || !gs) return;
    const colony = gs.colonies.find((c) => c.id === colonyId);
    if (!colony) return;
    const res = cancelPin(session(), app.pins, colony, pinIndex);
    if (res.error) showNote(`⛔ ${res.error}`);
    else {
      savePerGame();
      showNote(app.autopilot.enabled ? '✕ cancelled — colony returns to autopilot' : '✕ cancelled');
    }
  }

  // ---- star rename (needs a settlement in the system) ----
  let renamingStar = $state(false);
  let renameStarText = $state('');
  const focusNow = (el: HTMLElement) => el.focus();
  const canRenameSelected = $derived.by(() => {
    if (replay || !gs || !selected) return false;
    return gs.colonies.some((c) => {
      if (c.owner !== me()) return false;
      const p = gs.planets.find((x) => x.id === c.planetId);
      return p?.starId === selected.star.id;
    });
  });
  function startStarRename() {
    if (!selected) return;
    renamingStar = true;
    renameStarText = selected.star.name;
  }
  function commitStarRename() {
    if (!renamingStar || !selected) return;
    const name = renameStarText.trim();
    if (name && name !== selected.star.name) {
      session().submit('rename_star', { starId: selected.star.id, name });
    }
    renamingStar = false;
  }

  /** wormhole pairs (drawn once each) — only after an endpoint was visited
   * or lies in the scanner envelope; the fog must not leak the shortcuts */
  const wormholeLinks = $derived.by(() => {
    if (!gs) return [];
    const out: Array<{ x1: number; y1: number; x2: number; y2: number }> = [];
    for (const sv of view) {
      const s = sv.star;
      if (s.wormholeTo !== null && s.wormholeTo > s.id && sv.wormholeVisible) {
        const t = gs.stars.find((x) => x.id === s.wormholeTo);
        if (t) out.push({ x1: s.x, y1: s.y, x2: t.x, y2: t.y });
      }
    }
    return out;
  });

  // ---- invasion checklist (ui_streamlining.md friction A): the three
  // auto-landing preconditions from ground.ts, live, on the star panel —
  // war games used to end with transports orbiting empty because nothing
  // in the UI said the word "invade" or explained why nothing happened ----
  const invadeInfo = $derived.by(() => {
    if (!gs || !selected?.explored) return null;
    const myId = me();
    const target = gs.colonies.find((c) => {
      if (c.owner === myId || c.outpost) return false;
      const p = gs.planets.find((x) => x.id === c.planetId);
      return p?.starId === selected.star.id && areAtWar(gs, myId, c.owner);
    });
    if (!target) return null;
    const here = (s: (typeof gs.ships)[number]) => s.location.kind === 'star' && s.location.starId === selected.star.id;
    const transports = gs.ships.filter((s) => s.owner === myId && s.shipKind === 'transport' && here(s));
    const marines = transports.reduce((n, s) => n + shipMarines(s), 0);
    const defenders = gs.ships.filter(
      (s) => s.owner === target.owner && (s.shipKind === 'design' || s.shipKind === 'scout') && here(s),
    ).length;
    return { colony: target, transports: transports.length, marines, defenders, ready: marines > 0 && defenders === 0 };
  });

  /** per-star fleet markers: military vs civilian, sized by ship count */
  function fleetMarks(ships: Array<{ owner: number; kind: string }>): Array<{ owner: number; mil: number; civ: number }> {
    const byOwner = new Map<number, { owner: number; mil: number; civ: number }>();
    for (const s of ships) {
      const e = byOwner.get(s.owner) ?? { owner: s.owner, mil: 0, civ: 0 };
      if (s.kind === 'design') e.mil++;
      else e.civ++;
      byOwner.set(s.owner, e);
    }
    return [...byOwner.values()].sort((a, b) => a.owner - b.owner);
  }
  // sized to survive a zoomed-out reading without crowding the star art:
  // a lone scout is 17 map units, a big stack tops out around 32
  // (10 + 5√n was too easy to miss, 15 + 6√n drowned the stars)
  const markSize = (n: number) => 12 + Math.round(5 * Math.sqrt(n));

  const CLIMATE_COLORS: Record<string, string> = {
    gaia: '#5ee08a', terran: '#6cc862', arid: '#d8bb6a', swamp: '#7aa85a', ocean: '#4da3ff',
    tundra: '#bcd7e8', desert: '#e0a35e', barren: '#8f8a80', energized: '#c78bff', hostile: '#ff6b5e',
  };

  // ---- little worlds (bugs.md: "worlds in system view should look nicer") ----
  // each planet gets a shaded globe with climate-specific surface details and
  // a deterministic per-planet variant, instead of a flat color disc
  function mixHex(a: string, b: string, t: number): string {
    const pa = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16));
    const pb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16));
    return `#${pa.map((v, i) => Math.round(v + (pb[i]! - v) * t).toString(16).padStart(2, '0')).join('')}`;
  }
  interface WorldLook {
    base: string;
    light: string;
    dark: string;
    detail: string;
    pattern: 'seas' | 'bands' | 'craters' | 'ice';
    cap: boolean;
    /** deterministic tweak 0..3 shifting the detail placement */
    v: number;
  }
  const CLIMATE_PATTERNS: Record<string, Array<WorldLook['pattern']>> = {
    gaia: ['seas'], terran: ['seas'], ocean: ['seas'], swamp: ['seas', 'bands'],
    arid: ['bands', 'craters'], desert: ['bands', 'craters'], tundra: ['ice', 'craters'],
    barren: ['craters'], energized: ['bands', 'seas'], hostile: ['craters', 'bands'],
  };
  const DETAIL_TINT: Record<string, string> = {
    gaia: '#2f8f5a', terran: '#3f7fbf', ocean: '#2c6cc0', swamp: '#4d7a3a',
    arid: '#a8843c', desert: '#b07a3a', tundra: '#8fb8d8', barren: '#5d5a52',
    energized: '#8f5fd0', hostile: '#b03a30',
  };
  function worldLook(p: { id: number; climate: string }): WorldLook {
    const base = CLIMATE_COLORS[p.climate] ?? '#999';
    const h = ((p.id * 2654435761) >>> 0) % 1024;
    const patterns = CLIMATE_PATTERNS[p.climate] ?? ['craters'];
    return {
      base,
      light: mixHex(base, '#ffffff', 0.35),
      dark: mixHex(base, '#000000', 0.45),
      detail: DETAIL_TINT[p.climate] ?? mixHex(base, '#000000', 0.3),
      pattern: patterns[h % patterns.length]!,
      cap: p.climate === 'tundra' || p.climate === 'terran' || p.climate === 'gaia' ? h % 3 !== 0 : h % 5 === 0,
      v: h % 4,
    };
  }
  function mineralRing(m: string): { stroke: string; width: number; dash: string } | null {
    if (m === 'ultra_rich') return { stroke: '#ffd75e', width: 2.5, dash: '' };
    if (m === 'rich') return { stroke: '#ffd75e', width: 1.5, dash: '' };
    if (m === 'poor') return { stroke: '#777f9d', width: 1.2, dash: '3 3' };
    if (m === 'ultra_poor') return { stroke: '#565d78', width: 1.2, dash: '2 4' };
    return null;
  }

  interface StarVisual {
    spectral: string;
    common: string;
    coreR: number;
    coronaR: number;
    spikeLen: number;
    spikeCount: number;
    spikeAlpha: number;
    glow: number;
    hotCoreTint: number;
  }

  const STAR_VISUALS: Record<StarColor, StarVisual> = {
    // Approximate real classes by map color: hotter stars are larger + spikier.
    blue: { spectral: 'O/B', common: 'blue giant', coreR: 8.2, coronaR: 19, spikeLen: 25, spikeCount: 4, spikeAlpha: 0.48, glow: 0.28, hotCoreTint: 0.42 },
    white: { spectral: 'A/F', common: 'white main sequence', coreR: 7.5, coronaR: 17, spikeLen: 22, spikeCount: 4, spikeAlpha: 0.42, glow: 0.24, hotCoreTint: 0.36 },
    yellow: { spectral: 'G', common: 'yellow dwarf', coreR: 6.9, coronaR: 15, spikeLen: 18, spikeCount: 3, spikeAlpha: 0.32, glow: 0.2, hotCoreTint: 0.3 },
    orange: { spectral: 'K', common: 'orange dwarf/giant', coreR: 6.2, coronaR: 13.5, spikeLen: 14, spikeCount: 2, spikeAlpha: 0.2, glow: 0.16, hotCoreTint: 0.24 },
    red: { spectral: 'M', common: 'red dwarf/giant', coreR: 5.8, coronaR: 12.5, spikeLen: 11, spikeCount: 2, spikeAlpha: 0.15, glow: 0.14, hotCoreTint: 0.18 },
    brown: { spectral: 'L/T', common: 'brown dwarf', coreR: 5.1, coronaR: 10.5, spikeLen: 0, spikeCount: 0, spikeAlpha: 0, glow: 0.09, hotCoreTint: 0.12 },
    black_hole: { spectral: 'X', common: 'black hole', coreR: 0, coronaR: 0, spikeLen: 0, spikeCount: 0, spikeAlpha: 0, glow: 0, hotCoreTint: 0 },
    // core world: an emerald beacon nature never made — biggest glow on the map
    green: { spectral: '★', common: 'core world (victory star)', coreR: 8.6, coronaR: 21, spikeLen: 28, spikeCount: 6, spikeAlpha: 0.55, glow: 0.34, hotCoreTint: 0.46 },
  };

  const SPIKE_ANGLES: Record<StarColor, number[]> = {
    blue: [0, 45, 90, 135],
    white: [0, 45, 90, 135],
    yellow: [0, 60, 120],
    orange: [0, 90],
    red: [0, 90],
    brown: [],
    black_hole: [],
    green: [0, 30, 60, 90, 120, 150],
  };

  function starClassText(color: StarColor): string {
    const v = STAR_VISUALS[color];
    if (color === 'black_hole') return 'black hole';
    if (color === 'green') return 'CORE WORLD — hold every green star to win';
    return `${v.spectral}-class ${v.common}`;
  }

  function starSpikeStroke(color: StarColor): string {
    return mixHex(STAR_COLORS[color], '#ffffff', color === 'blue' ? 0.62 : 0.5);
  }
  const prettify = (id: string) => id.replaceAll('_', ' ');

  /** the one-world popup (bugs.md: click a planet to manage its production) */
  let managePlanetId = $state<number | null>(null);

  // ---- viewport budget: the map column must end ABOVE the sticky chat
  // footer, however many banners (auto-turn, host-offline, victory…) are
  // stacked above it — a hard-coded height pushed the map's horizontal
  // scrollbar under the footer whenever a banner appeared (bugs.md #1) ----
  let wrapEl = $state<HTMLDivElement | null>(null);
  let wrapH = $state(0);
  function measureWrap() {
    if (!wrapEl) return;
    const footer = document.querySelector('footer');
    const footerH = footer instanceof HTMLElement ? footer.offsetHeight : 0;
    const h = Math.floor(window.innerHeight - wrapEl.getBoundingClientRect().top - footerH - 10);
    if (Math.abs(h - wrapH) > 1) wrapH = h;
  }
  $effect(() => {
    void app.version; // banners come and go with game state — re-measure
    measureWrap();
  });
  $effect(() => {
    window.addEventListener('resize', measureWrap);
    const ro = new ResizeObserver(() => measureWrap());
    ro.observe(document.body);
    return () => {
      window.removeEventListener('resize', measureWrap);
      ro.disconnect();
    };
  });
</script>

<svelte:window onkeydown={onMapKey} />

<div class="wrap" bind:this={wrapEl} style={wrapH > 0 ? `height:${wrapH}px` : undefined}>
  <div class="mapcol">
    {#if app.autopilot.enabled && !scripted}
      <!-- autopilot players live on the map: the same sliders as the colony
           screen, so weights are adjustable without leaving it -->
      {#if !replay}<AutopilotBar />{/if}
    {/if}
    {#if mapNote}
      <div class="mapnote" data-testid="map-note">{mapNote}</div>
    {/if}
    <div
      class="scroller"
      class:zoomed={mapZoom === 'zoom'}
      class:dragging={panning}
      role="region"
      aria-label="Galaxy map viewport"
      bind:this={mapScroller}
      onpointerdown={onMapPointerDown}
      onpointermove={onMapPointerMove}
      onpointerup={endMapPan}
      onpointercancel={endMapPan}
    >
    <svg
      viewBox="0 0 {mapDims.w} {mapDims.h}"
      data-testid="galaxy-map"
      data-allow-ctrl-wheel-zoom="true"
      onwheel={onMapWheel}
      style={mapSvgStyle}
    >
      <defs>
        <filter id="starglow" x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur stdDeviation="6" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id="starsoft" x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur stdDeviation="2.8" />
        </filter>
        <radialGradient id="underdark">
          <stop offset="0%" stop-color="#020409" stop-opacity="0.55" />
          <stop offset="55%" stop-color="#020409" stop-opacity="0.38" />
          <stop offset="100%" stop-color="#020409" stop-opacity="0" />
        </radialGradient>
      </defs>

      <!-- dark pools under UNEXPLORED stars so their glyphs + names survive the
           colorful galaxy weather. Explored stars sit inside the territory
           tint, so this layer draws first and the fuel range washes OVER it -->
      <g class="underdark" aria-hidden="true">
        {#each view as v (v.star.id)}
          {#if !v.explored}
            <circle cx={v.star.x} cy={v.star.y} r="46" fill="url(#underdark)" />
          {/if}
        {/each}
      </g>

      <!-- fuel range as ONE muted territory in the player color: solid circles
           inside a group whose opacity applies AFTER compositing, so overlaps
           never darken and the union reads as a single region -->
      <g class="territory" style="fill:{playerColor(me())}">
        {#each rangeCircles as rc, i (i)}
          <circle cx={rc.x} cy={rc.y} r={rc.r} />
        {/each}
      </g>

      {#each wormholeLinks as wl, i (i)}
        <line x1={wl.x1} y1={wl.y1} x2={wl.x2} y2={wl.y2} class="wormhole">
          <title>wormhole — 1 turn transit either way</title>
        </line>
      {/each}

      {#each transits as t (t.id)}
        <line x1={t.x} y1={t.y} x2={t.tx} y2={t.ty} class="route" style="stroke:{playerColor(me())}" />
        <g transform="translate({t.x},{t.y}) rotate({t.angle})">
          <!-- in-flight ships wear the same player color as everything else;
               re-routable fleets keep the yellow fill as the affordance -->
          <polygon points="20,0 -13,-12 -7,0 -13,12" class="fleetmark" class:reroutable={t.reroutable} style="fill:{t.reroutable ? '' : playerColor(me())}" />
        </g>
        <text x={t.x} y={t.y - 18} text-anchor="middle" class="eta" fill={playerColor(me())}>{t.name} · {t.eta}t</text>
      {/each}

      {#each view as v (v.star.id)}
        {@const kinds = monstersByStar.get(v.star.id)}
        {@const sv = STAR_VISUALS[v.star.color]}
        {@const scale = v.explored ? 1 : 0.82}
        {@const settler = v.colonies.find((c) => !c.outpost)}
        <g
          class="star"
          data-testid="star-{v.star.id}"
          role="button"
          tabindex="0"
          onclick={() => clickStar(v.star.id)}
          onkeydown={(e) => e.key === 'Enter' && clickStar(v.star.id)}
          transform="translate({v.star.x},{v.star.y})"
        >
          <title>
            {v.star.name}
            {' — ' + starClassText(v.star.color)}
            {v.explored ? '' : ' — unexplored'}
            {reachable.has(v.star.id) ? '' : ' — out of fuel range'}
          </title>
          <!-- invisible hit target so the whole star area is clickable -->
          <circle r="30" fill="transparent" stroke="none" />
          {#if v.star.id === selectedStarId}
            <circle r="36" class="selring" />
          {/if}
          {#if selectedShipIds.length > 0 && v.star.id !== selectedStarId && (reachable.has(v.star.id) || v.star.id === wormholeTarget)}
            <circle r="30" class="target" />
          {/if}
          {#if !reachable.has(v.star.id)}
            <circle r="26" class="norange" />
          {/if}
          {#if v.star.color === 'black_hole'}
            <ellipse rx="16" ry="7.2" fill="none" stroke="#704c8a" stroke-width="2.6" opacity={v.explored ? 0.78 : 0.45} transform="rotate(-24)" />
            <ellipse rx="13.2" ry="6" fill="none" stroke="#ad89d9" stroke-width="1.2" opacity={v.explored ? 0.58 : 0.32} transform="rotate(-24)" />
            <circle r="8.4" fill="#02030a" stroke="#8a76b5" stroke-width="2.2" opacity={v.explored ? 1 : 0.5} />
            <circle r="4.4" fill="#000000" />
          {:else}
            <circle
              r={sv.coronaR}
              fill={mixHex(STAR_COLORS[v.star.color], '#ffffff', sv.hotCoreTint * 0.5)}
              opacity={v.explored ? sv.glow : Math.max(0.07, sv.glow * 0.5)}
              filter={v.explored ? 'url(#starglow)' : 'url(#starsoft)'}
            />
            <circle
              r={sv.coreR * 1.06 * scale}
              fill={STAR_COLORS[v.star.color]}
              opacity={v.explored ? 0.95 : 0.42}
            />
            <circle
              r={sv.coreR * 0.56 * scale}
              fill={mixHex(STAR_COLORS[v.star.color], '#ffffff', sv.hotCoreTint)}
              opacity={v.explored ? 0.88 : 0.5}
            />
            {#if sv.spikeCount > 0}
              {#each SPIKE_ANGLES[v.star.color] as ang, ai (ai)}
                <line
                  x1={-sv.spikeLen * scale}
                  y1="0"
                  x2={sv.spikeLen * scale}
                  y2="0"
                  stroke={starSpikeStroke(v.star.color)}
                  stroke-width={ang % 90 === 0 ? 1.45 : 1.05}
                  opacity={v.explored ? sv.spikeAlpha : sv.spikeAlpha * 0.45}
                  transform="rotate({ang})"
                  stroke-linecap="round"
                />
              {/each}
            {/if}
          {/if}
          {#if !v.explored}
            <circle r="21" class="fog" />
            <text y="7" text-anchor="middle" class="unknown">?</text>
          {/if}
          {#each v.colonies.filter((c) => !c.outpost) as c, i (c.id)}
            <circle r={22 + i * 5} fill="none" stroke={playerColor(c.owner)} stroke-width="3" opacity="0.9" />
          {/each}
          <!-- outposts used to be invisible here: a thin dashed ring marks
               "ours, but only a fuel stop" without claiming colony weight -->
          {#each v.colonies.filter((c) => c.outpost) as c, i (c.id)}
            <circle r={19 + i * 4} fill="none" stroke={playerColor(c.owner)} stroke-width="1.6" stroke-dasharray="5 6" opacity="0.85">
              <title>outpost — fuel range only</title>
            </circle>
          {/each}
          {#each fleetMarks(v.ships) as fm, i (fm.owner)}
            {@const y0 = -15 + i * 27}
            {#if fm.mil > 0}
              {@const s = markSize(fm.mil)}
              <polygon
                points="20,{y0} {20 + s},{y0 + s / 2} 20,{y0 + s}"
                fill={playerColor(fm.owner)}
                stroke="#05070f"
                stroke-width="1.5"
              >
                <title>{fm.mil} warship{fm.mil > 1 ? 's' : ''}</title>
              </polygon>
              {#if fm.mil > 1}
                <text x={22 + s} y={y0 + s / 2 + 6} class="count" fill={playerColor(fm.owner)}>{fm.mil}</text>
              {/if}
            {/if}
            {#if fm.civ > 0}
              {@const s = markSize(fm.civ)}
              {@const yc = y0 + (fm.mil > 0 ? markSize(fm.mil) + 3 : 0)}
              <polygon
                points="20,{yc} {20 + s},{yc + s / 2} 20,{yc + s}"
                fill="none"
                stroke={playerColor(fm.owner)}
                stroke-width="2.5"
              >
                <title>{fm.civ} civilian ship{fm.civ > 1 ? 's' : ''} (scouts/colony/transport)</title>
              </polygon>
              {#if fm.civ > 1}
                <text x={22 + s} y={yc + s / 2 + 6} class="count" fill={playerColor(fm.owner)}>{fm.civ}</text>
              {/if}
            {/if}
          {/each}
          {#if v.explored && kinds}
            {#if isRaid(kinds)}
              <text y="-24" text-anchor="middle" class="raid">⚠</text>
            {:else}
              <text y="-24" text-anchor="middle" class="monster">☠</text>
            {/if}
          {/if}
          {#if blockadedStars.has(v.star.id)}
            <text x="-32" y="7" text-anchor="middle" class="blockade">⚓</text>
          {/if}
          <!-- settled systems wear their owner's color on the name itself:
               the strongest zoomed-out signal for "whose space is this" -->
          <text
            y="38"
            text-anchor="middle"
            class="label"
            class:dimlabel={!v.explored}
            style:fill={settler ? playerColor(settler.owner) : undefined}
          >{v.star.name}</text>
          {#if v.explored && v.planets.length}
            {@const bodies = v.planets.slice(0, 6)}
            {#each bodies as p, bi (p.id)}
              {@const bx = (bi - (bodies.length - 1) / 2) * 9}
              {#if p.body === 'planet'}
                {@const col = v.colonies.find((c) => gs?.colonies.find((x) => x.id === c.id)?.planetId === p.id)}
                <!-- settled worlds are SOLID owner-colored dots, open worlds
                     are hollow rings: colonization state at a glance -->
                {#if col}
                  <circle cx={bx} cy="50" r="3.2" fill={playerColor(col.owner)} stroke="#05070f" stroke-width="0.8">
                    <title>{p.climate} planet — {col.name}{col.outpost ? ' (outpost)' : ''}</title>
                  </circle>
                {:else}
                  <circle cx={bx} cy="50" r="2.8" fill="none" stroke="#8a93ad" stroke-width="1.1">
                    <title>{p.climate} planet (uncolonized)</title>
                  </circle>
                {/if}
              {:else}
                <text x={bx} y="53" text-anchor="middle" class="bodyx"><title>{p.body === 'asteroids' ? 'asteroid belt' : 'gas giant'}</title>×</text>
              {/if}
            {/each}
          {/if}
        </g>
      {/each}
    </svg>
    </div>
    <div class="legend">
      <button
        class="zoombtn"
        data-testid="map-zoom-toggle"
        title="toggle between fitting the whole galaxy and a scrollable close-up"
        onclick={toggleMapZoom}
      >{mapZoom === 'fit' ? '🔍 zoom in' : '🗺 fit galaxy'}</button>
      <label><input type="checkbox" bind:checked={showRange} /> fuel range</label>
      <span><span class="sw" style="border-color:#5a3030"></span> dashed ring = out of range</span>
      <span class="dimtext">◐ faded star = unexplored</span>
      <span><span class="monster">☠</span> monster lair</span>
      <span><span class="raid">⚠</span> Andromedan raid</span>
      <span>▶ fleet under way (label shows ETA)</span>
      <span>▲ solid = warships · △ hollow = civilians</span>
      <span style="color:#b78bff">┈ wormhole</span>
      <span class="dimtext" title="select one of your stars and press B, then one key per ship/building">⌨ B = build here</span>
      <label title="idle scouts fly themselves to the nearest unexplored star in fuel range each turn — ordinary move orders you can re-route or override">
        <input
          type="checkbox"
          data-testid="auto-explore"
          checked={app.autoExplore}
          onchange={(e) => {
            app.autoExplore = (e.target as HTMLInputElement).checked;
            savePerGame();
          }}
        />
        🔭 auto-explore scouts
      </label>
    </div>
    {#if pinStatuses.length}
      <!-- quick-build status: one bar per pinned item, refreshed every turn -->
      <div class="pinned" data-testid="pinned-builds">
        {#each pinStatuses as ps (`${ps.colonyId}:${ps.pinIndex}`)}
          <div class="pin" data-testid="pinned-{ps.colonyId}-{ps.pinIndex}">
            <button class="jump" title="show {ps.starName} on the map" onclick={() => (selectedStarId = ps.starId)}>{ps.colonyName}</button>
            <span class="plabel">{ps.label}{ps.more > 0 ? ` +${ps.more} queued` : ''}</span>
            <span
              class="pbar"
              title="{ps.pct}% built{ps.turns !== null ? ` · ~${ps.turns} turn${ps.turns > 1 ? 's' : ''} to go` : ' · stalled (no production reaches the queue)'}{ps.queuePos > 0 ? ` · waits behind ${ps.queuePos} item${ps.queuePos > 1 ? 's' : ''}` : ''}"
            ><span class="pfill" style="width:{ps.pct}%"></span></span>
            <span class="peta">{ps.queuePos > 0 ? `#${ps.queuePos + 1}` : ps.turns !== null ? `~${ps.turns}t` : '⏸'}</span>
            <button
              class="pcancel"
              data-testid="cancel-pin-{ps.colonyId}-{ps.pinIndex}"
              title="cancel this build — the colony goes back to autobuild"
              onclick={() => cancelPinnedBuild(ps.colonyId, ps.pinIndex)}
            >✕</button>
          </div>
        {/each}
      </div>
    {/if}
  </div>

  <aside>
    {#if selected}
      <h3 data-testid="selected-star">
        {#if renamingStar}
          <input
            class="renamestar"
            data-testid="rename-star-input"
            bind:value={renameStarText}
            use:focusNow
            maxlength="24"
            onkeydown={(e) => {
              if (e.key === 'Enter') commitStarRename();
              else if (e.key === 'Escape') renamingStar = false;
            }}
            onblur={commitStarRename}
          />
        {:else}
          {selected.star.name} <span class="dim">({selected.star.color.replaceAll('_', ' ')})</span>
          {#if canRenameSelected}
            <button class="mini ghost" data-testid="rename-star" title="rename star (needs a settlement here)" onclick={startStarRename}>✏️</button>
          {/if}
        {/if}
      </h3>
      {#if !selected.explored}
        <p class="dim">unexplored — send a ship to chart this system</p>
      {/if}
      {#if selected.star.wormholeTo !== null}
        <p class="dim">🌀 wormhole link — 1 turn transit</p>
      {/if}
      {#if !reachable.has(selected.star.id)}
        <p class="warn">⛽ out of fuel range — extend range with fuel-cell tech or a closer colony/outpost</p>
      {/if}
      {#if selected.explored && monstersByStar.has(selected.star.id)}
        {@const kinds = monstersByStar.get(selected.star.id)!}
        {#if isRaid(kinds)}
          <p class="raid" data-testid="monster-warning">⚠ Andromedan raid in progress: {kinds.map(prettify).join(', ')}</p>
        {:else}
          <p class="monster" data-testid="monster-warning">☠ guarded by: {kinds.map(prettify).join(', ')} — destroy the keeper to settle here</p>
        {/if}
      {/if}
      {#if blockadedStars.has(selected.star.id)}
        <p class="blockade">⚓ blockaded — output halved, no freighter food</p>
      {/if}
      {#if peacefulForeigners.length}
        <p class="dim">🕊 {peacefulForeigners.join(', ')} ships present — you are at peace. Declare war on the Empires tab to engage.</p>
      {/if}
      {#if selected.explored && selected.planets.length}
        <!-- classic system view: star + orbit arcs, planet size/climate/richness at a glance -->
        <svg class="system" viewBox="0 0 330 92" aria-label="system view">
          <g transform="translate(-26,46)">
            {#if selected.star.color === 'black_hole'}
              <ellipse rx="56" ry="18" fill="none" stroke="#6c4f8e" stroke-width="4.5" opacity="0.7" transform="rotate(-24)" />
              <ellipse rx="47" ry="14" fill="none" stroke="#c6a2ec" stroke-width="2" opacity="0.5" transform="rotate(-24)" />
              <circle r="39" fill="#02030a" stroke="#8a76b5" stroke-width="3.5" />
              <circle r="18" fill="#000000" />
            {:else}
              <circle r={STAR_VISUALS[selected.star.color].coronaR * 2.7} fill={mixHex(STAR_COLORS[selected.star.color], '#ffffff', STAR_VISUALS[selected.star.color].hotCoreTint * 0.5)} opacity="0.24" filter="url(#starglow)" />
              <circle r={STAR_VISUALS[selected.star.color].coreR * 2.55} fill={STAR_COLORS[selected.star.color]} opacity="0.96" />
              <circle r={STAR_VISUALS[selected.star.color].coreR * 1.35} fill={mixHex(STAR_COLORS[selected.star.color], '#ffffff', STAR_VISUALS[selected.star.color].hotCoreTint)} opacity="0.9" />
              {#if STAR_VISUALS[selected.star.color].spikeCount > 0}
                {#each SPIKE_ANGLES[selected.star.color] as ang, ai (ai)}
                  <line
                    x1={-STAR_VISUALS[selected.star.color].spikeLen * 2.1}
                    y1="0"
                    x2={STAR_VISUALS[selected.star.color].spikeLen * 2.1}
                    y2="0"
                    stroke={starSpikeStroke(selected.star.color)}
                    stroke-width={ang % 90 === 0 ? 2.2 : 1.7}
                    opacity={STAR_VISUALS[selected.star.color].spikeAlpha * 0.55}
                    transform="rotate({ang})"
                    stroke-linecap="round"
                  />
                {/each}
              {/if}
            {/if}
          </g>
          {#each [1, 2, 3, 4, 5] as orbit (orbit)}
            {@const ox = 30 + orbit * 56}
            <circle cx="-26" cy="46" r={ox + 26} fill="none" stroke="#26304f" stroke-width="1" />
            {#each selected.planets.filter((p) => p.orbit === orbit) as p (p.id)}
              {@const px = ox + 4}
              {#if p.body === 'asteroids'}
                {#each [-8, -3, 2, 7, 12] as off, ai (ai)}
                  <circle cx={px + off} cy={46 + ((ai * 7) % 11) - 5} r="1.6" fill="#8f8a80" />
                {/each}
              {:else if p.body === 'gas_giant'}
                <circle cx={px} cy="46" r="13" fill="url(#gg-{p.id})" />
                <g clip-path="url(#clip-{p.id})" opacity="0.5">
                  <ellipse cx={px} cy="42" rx="14" ry="2.2" fill="#a87c46" />
                  <ellipse cx={px} cy="49" rx="14" ry="1.8" fill="#e6c898" />
                </g>
                <ellipse cx={px} cy="46" rx="17" ry="4" fill="none" stroke="#e0c090" stroke-width="1.2" opacity="0.7" />
                <defs>
                  <radialGradient id="gg-{p.id}" cx="0.35" cy="0.3" r="1">
                    <stop offset="0%" stop-color="#e8c894" /><stop offset="70%" stop-color="#c9a06a" /><stop offset="100%" stop-color="#6e5432" />
                  </radialGradient>
                  <clipPath id="clip-{p.id}"><circle cx={px} cy="46" r="13" /></clipPath>
                </defs>
              {:else}
                {@const ring = mineralRing(p.minerals)}
                {@const look = worldLook(p)}
                {@const pr = 4 + p.sizeClass * 1.8}
                <defs>
                  <radialGradient id="pg-{p.id}" cx="0.35" cy="0.3" r="1.05">
                    <stop offset="0%" stop-color={look.light} />
                    <stop offset="55%" stop-color={look.base} />
                    <stop offset="100%" stop-color={look.dark} />
                  </radialGradient>
                  <clipPath id="clip-{p.id}"><circle cx={px} cy="46" r={pr} /></clipPath>
                </defs>
                <circle cx={px} cy="46" r={pr} fill="url(#pg-{p.id})" />
                <g clip-path="url(#clip-{p.id})">
                  {#if look.pattern === 'seas'}
                    <!-- continents / seas: irregular blobs, placement varies per world -->
                    <ellipse cx={px - pr * 0.35 + look.v} cy={46 - pr * 0.2} rx={pr * 0.55} ry={pr * 0.35} fill={look.detail} opacity="0.75" transform="rotate({look.v * 17} {px} 46)" />
                    <ellipse cx={px + pr * 0.4 - look.v * 0.5} cy={46 + pr * 0.35} rx={pr * 0.4} ry={pr * 0.22} fill={look.detail} opacity="0.65" transform="rotate({-look.v * 11} {px} 46)" />
                  {:else if look.pattern === 'bands'}
                    <!-- latitude bands (dune belts, storm streaks) -->
                    <ellipse cx={px} cy={46 - pr * 0.35} rx={pr * 1.1} ry={pr * 0.16} fill={look.detail} opacity="0.6" transform="rotate({look.v * 4 - 6} {px} 46)" />
                    <ellipse cx={px} cy={46 + pr * 0.25} rx={pr * 1.1} ry={pr * 0.2} fill={look.detail} opacity="0.5" transform="rotate({look.v * 4 - 6} {px} 46)" />
                  {:else if look.pattern === 'craters'}
                    <circle cx={px - pr * 0.3} cy={46 - pr * 0.25 + look.v * 0.6} r={pr * 0.2} fill={look.dark} opacity="0.8" />
                    <circle cx={px + pr * 0.35} cy={46 + pr * 0.15} r={pr * 0.14} fill={look.dark} opacity="0.7" />
                    <circle cx={px + pr * 0.05 + look.v * 0.4} cy={46 + pr * 0.45} r={pr * 0.1} fill={look.detail} opacity="0.8" />
                  {:else if look.pattern === 'ice'}
                    <ellipse cx={px} cy={46 - pr * 0.55} rx={pr * 0.8} ry={pr * 0.35} fill="#eef6ff" opacity="0.85" />
                    <ellipse cx={px - pr * 0.2} cy={46 + pr * 0.3} rx={pr * 0.4} ry={pr * 0.18} fill={look.detail} opacity="0.5" />
                  {/if}
                  {#if look.cap}
                    <ellipse cx={px} cy={46 - pr * 0.78} rx={pr * 0.5} ry={pr * 0.2} fill="#f2f8ff" opacity="0.9" />
                  {/if}
                  <!-- night-side terminator -->
                  <circle cx={px + pr * 0.45} cy={46 + pr * 0.4} r={pr * 1.15} fill="#04060e" opacity="0.28" />
                </g>
                {#if ring}
                  <circle cx={px} cy="46" r={7 + p.sizeClass * 1.8} fill="none" stroke={ring.stroke} stroke-width={ring.width} stroke-dasharray={ring.dash} />
                {/if}
                {#each selected.colonies.filter((c) => gs?.colonies.find((x) => x.id === c.id)?.planetId === p.id) as c (c.id)}
                  <circle cx={px} cy="46" r={10 + p.sizeClass * 1.8} fill="none" stroke={playerColor(c.owner)} stroke-width="1.8" />
                {/each}
              {/if}
            {/each}
          {/each}
        </svg>
        <p class="syskey">size = circle · color = climate · thin gold ring = rich · THICK gold = ultra-rich · dashed = poor · player ring = colony</p>
      {/if}
      {#if selected.ships.some((sh) => sh.owner !== me())}
        {@const foes = selected.ships.filter((sh) => sh.owner !== me() && sh.owner >= 0)}
        {#if foes.length}
          <p class="foefleet" data-testid="enemy-fleet-{selected.star.id}">
            ⚔ enemy fleet:
            {#each Object.entries(foes.reduce((acc: Record<string, number>, sh) => { const k = `${foeName(sh.owner)} ${sh.hull ?? sh.kind}`; acc[k] = (acc[k] ?? 0) + 1; return acc; }, {})) as [label, n] (label)}
              <span class="foechip">{n}× {label}</span>
            {/each}
          </p>
        {/if}
      {/if}
      {#if invadeInfo}
        <div class="invade" data-testid="invade-checklist">
          <b>🪖 Invading {invadeInfo.colony.name}</b>
          <ul>
            <li class:ok={invadeInfo.marines > 0}>
              {#if invadeInfo.marines > 0}
                ✓ {invadeInfo.marines} marine{invadeInfo.marines > 1 ? 's' : ''} aboard {invadeInfo.transports} transport{invadeInfo.transports > 1 ? 's' : ''} here
              {:else if invadeInfo.transports > 0}
                ✗ your transports here are EMPTY — load marines at one of your colonies (select the fleet, press L)
              {:else}
                ✗ no transports here — build transports, load marines at your colony (L), fly them in
              {/if}
            </li>
            <li class:ok={invadeInfo.defenders === 0}>
              {invadeInfo.defenders === 0
                ? '✓ the sky is clear'
                : `✗ ${invadeInfo.defenders} defending warship${invadeInfo.defenders > 1 ? 's' : ''} in orbit — clear them first`}
            </li>
            {#if invadeInfo.ready}
              <li class="ok">▶ landing resolves automatically at the next turn boundary</li>
            {/if}
          </ul>
        </div>
      {/if}
      <ul class="planets">
        {#each selected.planets as p (p.id)}
          <li data-testid="planet-{p.id}">
            <span class="orbit">{p.orbit}</span>
            <PixelPlanet seed={p.id} climate={p.climate} body={p.body} size={16} />
            <!-- clicking a world opens its popup: planet facts, and the full
                 production picture when it is your colony (bugs.md) -->
            <button
              class="plink"
              data-testid="planet-open-{p.id}"
              title="open this world — facts, and production management if it is your colony"
              onclick={() => (managePlanetId = p.id)}
            >{p.body === 'planet' ? `${p.climate} · size ${p.sizeClass} · ${prettify(p.minerals)} · ${p.gravity}-g` : prettify(p.body)}</button>
            {#each selected.colonies.filter((c) => gs?.colonies.find((x) => x.id === c.id)?.planetId === p.id) as c (c.id)}
              <button class="plink" style="color:{playerColor(c.owner)}" title="open {c.name}" onclick={() => (managePlanetId = p.id)}><b> — {c.name}</b></button>
              {#if c.owner === me() && c.outpost}
                <button
                  data-testid="scrap-outpost-{c.id}"
                  title="dismantle this outpost for 25 BC salvage (its fuel-range support goes with it)"
                  onclick={() => !replay && session().submit('scrap_outpost', { colonyId: c.id })}
                >🗑 scrap outpost</button>
              {/if}
              {#if !c.outpost && (c.owner === me() || shipsHere.length > 0)}
                <!-- the planet's one fixed terrain map: owners always see it;
                     anyone else needs a ship (even a scout) at the star -->
                <details class="terrainbox" data-testid="terrain-{p.id}">
                  <summary title="every planet keeps ONE fixed terrain map — invasions are fought over it, and doctrine/tactic matchups care about the ground">🗺 terrain</summary>
                  <span class="terrainrow">
                    <svg viewBox="0 0 {TERRAIN_W * 8} {TERRAIN_H * 8}" class="minimap" aria-label="terrain map">
                      {#each generateTerrain(p.id, p.climate) as trow, ty (ty)}
                        {#each trow.split('') as ch, tx (tx)}
                          <rect x={tx * 8} y={ty * 8} width="8" height="8" fill={TERRAIN_INFO[ch]?.color ?? '#666'}>
                            <title>{TERRAIN_INFO[ch]?.name}{TERRAIN_INFO[ch]?.defBonus ? ` (+${Math.round((TERRAIN_INFO[ch]?.defBonus ?? 0) * 100)}% defense)` : ''}</title>
                          </rect>
                        {/each}
                      {/each}
                    </svg>
                    {#if c.owner === me()}
                      <label class="doctrine" title="standing ground doctrine for this colony — the terrain and the invader's tactic decide how well it holds">
                        🛡
                        <select
                          data-testid="doctrine-{c.id}"
                          value={gs?.colonies.find((x) => x.id === c.id)?.groundTactic ?? ''}
                          onchange={(e) => {
                            const v = (e.target as HTMLSelectElement).value;
                            if (!replay) session().submit('set_ground_tactic', { colonyId: c.id, tactic: v || null });
                          }}
                        >
                          <option value="">standard defense</option>
                          {#each DEFENSE_TACTICS as t (t)}
                            <option value={t}>{t.replaceAll('_', ' ')}</option>
                          {/each}
                        </select>
                      </label>
                    {/if}
                  </span>
                </details>
              {/if}
            {/each}
            {#each shipsHere as f (f.ship.id)}
              {#if f.canColonizeHere.includes(p.id)}
                <button data-testid="colonize-{p.id}" onclick={() => colonize(f.ship.id, p.id)}>colonize</button>
              {:else if f.canOutpostHere.includes(p.id)}
                <button onclick={() => outpost(f.ship.id, p.id)}>outpost</button>
              {:else if f.canConstructHere.includes(p.id)}
                <button
                  data-testid="construct-{p.id}"
                  title="rebuild this body into a barren world (consumes the construction ship)"
                  onclick={() => !replay && session().submit('construct_planet', { shipId: f.ship.id, planetId: p.id })}
                >construct planet</button>
              {/if}
            {/each}
          </li>
        {/each}
      </ul>
      {#if myColonyHere && !scripted}
        {#if buildArm}
          <div class="buildarm" data-testid="build-arm">
            🔨 <b>Build at {bestYardHere?.name ?? '…'}</b> — press a key:
            <span class="keylist">
              <kbd>C</kbd> colony ship · <kbd>S</kbd> scout · <kbd>F</kbd> frigate · <kbd>D</kbd> destroyer ·
              <kbd>R</kbd> cruiser · <kbd>B</kbd> battleship · <kbd>T</kbd> titan · <kbd>O</kbd> outpost ship ·
              <kbd>H</kbd> housing · <kbd>A</kbd> factory · <kbd>L</kbd> research lab · <kbd>K</kbd> supercomputer
            </span>
            <button class="mini" onclick={() => (buildArm = false)}>done (Esc)</button>
          </div>
        {:else}
          <p class="dim">🔨 press <b>B</b> to queue builds here by hotkey (ships use your latest designs)</p>
        {/if}
        <select
          class="buildpick"
          data-testid="quick-build-select"
          value=""
          title="queue an item at this system's best-suited colony — it is pinned: autopilot won't touch the yard until it completes (cancel from the build list under the map)"
          onchange={(e) => {
            const v = (e.target as HTMLSelectElement).value;
            if (v && gs) queuePinnedItem(v, itemLabel(gs, me(), v));
            (e.target as HTMLSelectElement).value = '';
          }}
        >
          <option value="">+ queue a build at the best colony…</option>
          {#each buildOptions as o (o.id)}
            <option value={o.id}>{o.label}</option>
          {/each}
        </select>
      {/if}
      {#if shipsHere.length}
        <h4>
          Your ships here <span class="dim keys" title="hotkeys: A selects all/none · Backspace cycles single ships (then all) · C colonize / O outpost the biggest open planet · L load / U unload a transport at the colony">A=all · ⌫=cycle · C/O=settle · L/U=transports</span>
          <label class="selall" title="select every ship at this system (hotkey: A)">
            <input
              type="checkbox"
              data-testid="select-all-ships"
              checked={shipsHere.every((f) => selectedShipIds.includes(f.ship.id))}
              onchange={(e) => {
                const ids = shipsHere.map((f) => f.ship.id);
                selectedShipIds = (e.target as HTMLInputElement).checked
                  ? [...new Set([...selectedShipIds, ...ids])]
                  : selectedShipIds.filter((id) => !ids.includes(id));
              }}
            />
            all
          </label>
        </h4>
        <ul class="ships">
          {#each shipsHere as f (f.ship.id)}
            <li>
              <label>
                <input type="checkbox" checked={selectedShipIds.includes(f.ship.id)} onchange={() => toggleShip(f.ship.id)} />
                {f.name} <span class="dim">#{shortEntityId(f.ship.id)}</span>
              </label>
            </li>
          {/each}
        </ul>
        {#if selectedShipIds.length}
          <p class="go">➤ click a destination star to move {selectedShipIds.length} ship{selectedShipIds.length > 1 ? 's' : ''} — green halo = in range</p>
        {/if}
      {/if}
      {#if fleets.some((f) => f.reroutable)}
        <p class="dim">↩ fleets ordered this turn can still be re-routed from the Fleets tab.</p>
      {/if}
    {:else}
      <p class="dim">select a star</p>
      {#if showFleetTip}
        <p class="dim">
          Your ships travel star-to-star within fuel range. Fleets under way show as ▶ markers with their ETA.
          <button class="tipdismiss" onclick={dismissFleetTip}>got it ✕</button>
        </p>
      {/if}
    {/if}
  </aside>
</div>

{#if managePlanetId !== null && !replay}
  <!-- live games only: the dialog reads the active session (a replay has
       none — its aside panel already shows the same facts) -->
  <PlanetDialog planetId={managePlanetId} onclose={() => (managePlanetId = null)} />
{/if}

<style>
  .bodyx {
    font-size: 8px;
    fill: var(--text-dim);
  }
  /* ---- quick-build: armed hint, dropdown, pinned status bars ---- */
  .buildarm {
    background: linear-gradient(180deg, var(--panel-3), var(--panel-3));
    border: 1px solid var(--accent-soft, #6c86d8);
    border-radius: 8px;
    padding: 0.4rem 0.6rem;
    font-size: 0.8rem;
    margin: 0.3rem 0;
  }
  .buildarm .keylist {
    display: block;
    opacity: 0.85;
    margin: 0.25rem 0;
    line-height: 1.5;
  }
  .buildarm kbd {
    background: var(--panel);
    border: 1px solid var(--line-bright, var(--line-bright));
    border-radius: 4px;
    padding: 0 0.28rem;
    font-size: 0.72rem;
  }
  .buildpick {
    max-width: 100%;
    margin-bottom: 0.4rem;
  }
  .mini {
    font-size: 0.72rem;
    padding: 0.05rem 0.35rem;
  }
  .pinned {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    margin-top: 0.35rem;
    background: var(--panel);
    border: 1px solid var(--line);
    border-radius: 8px;
    padding: 0.3rem 0.55rem;
    font-size: 0.78rem;
  }
  .pin {
    display: flex;
    align-items: center;
    gap: 0.45rem;
  }
  .pin .jump {
    background: transparent;
    border: none;
    color: var(--accent-soft, #8fa8ff);
    cursor: pointer;
    padding: 0;
    font: inherit;
    text-decoration: underline dotted;
    min-width: 7rem;
    text-align: left;
  }
  .pin .plabel {
    min-width: 10rem;
  }
  .pin .pbar {
    flex: 1;
    max-width: 14rem;
    height: 0.5rem;
    background: var(--panel);
    border: 1px solid var(--line);
    border-radius: 4px;
    overflow: hidden;
  }
  .pin .pfill {
    display: block;
    height: 100%;
    background: linear-gradient(90deg, #2c7a4e, var(--good, var(--good)));
    transition: width 0.4s ease;
  }
  .pin .peta {
    min-width: 2.6rem;
    text-align: right;
    color: var(--text-dim, var(--text-dim));
    font-variant-numeric: tabular-nums;
  }
  .pin .pcancel {
    font-size: 0.72rem;
    padding: 0 0.3rem;
  }
  .tipdismiss {
    font-size: 0.72rem;
    padding: 0 0.3rem;
    margin-left: 0.4rem;
  }
  .selall {
    font-size: 0.75rem;
    font-weight: 400;
    color: var(--text-dim);
    margin-left: 0.5rem;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
  }
  .keys {
    font-size: 0.68rem;
    font-weight: 400;
    letter-spacing: 0.02em;
  }
  .foefleet {
    font-size: 0.8rem;
    color: var(--bad, #ff7b7b);
  }
  .foechip {
    background: color-mix(in srgb, var(--bad) 12%, transparent);
    border: 1px solid color-mix(in srgb, var(--bad) 35%, transparent);
    border-radius: 6px;
    padding: 0 0.3rem;
    margin-right: 0.25rem;
  }
  .invade {
    font-size: 0.82rem;
    border: 1px solid color-mix(in srgb, var(--gold) 45%, transparent);
    border-radius: 8px;
    padding: 0.35rem 0.5rem;
    margin: 0.3rem 0;
  }
  .invade ul {
    list-style: none;
    margin: 0.2rem 0 0;
    padding: 0;
  }
  .invade li {
    color: var(--text-dim);
  }
  .invade li.ok {
    color: var(--good, #7be07b);
  }
  .wrap {
    display: flex;
    gap: 0.8rem;
    /* fallback before the first measurement: the real height is measured at
       runtime (banners above + sticky footer below vary), so the map's own
       scrollbars always stay on-screen */
    height: calc(100dvh - 14rem);
    min-height: 18rem;
  }
  .mapcol {
    flex: 1;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  .mapnote {
    background: #33201a;
    border: 1px solid var(--bad, var(--bad));
    color: var(--bad, var(--bad));
    border-radius: 6px;
    padding: 0.3rem 0.6rem;
    margin-bottom: 0.4rem;
    font-size: 0.85rem;
  }
  svg {
    background:
      radial-gradient(70% 60% at 60% 30%, color-mix(in srgb, var(--panel-3) 35%, transparent) 0%, transparent 70%),
      var(--bg);
    border: 1px solid var(--line);
    border-radius: 10px;
    min-height: 0;
    display: block;
  }
  /* zoom-to-fit really FITS: the whole galaxy stays inside the viewport
     height instead of only matching the width and scrolling vertically */
  .scroller {
    min-height: 0;
    flex: 1;
  }
  .scroller:not(.zoomed) {
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }
  .scroller:not(.zoomed) svg {
    height: auto;
    margin: 0;
  }
  .scroller.zoomed {
    overflow: auto;
    max-height: 74vh;
    border-radius: 10px;
    cursor: grab;
  }
  .scroller.zoomed svg {
    min-height: 420px;
  }
  .scroller.zoomed.dragging {
    cursor: grabbing;
    user-select: none;
  }
  .zoombtn {
    font-size: 0.8rem;
  }
  /* planet rows open the one-world popup: styled as text, clickable */
  .plink {
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    font: inherit;
    color: inherit;
    cursor: pointer;
    text-align: left;
  }
  .plink:hover {
    text-decoration: underline;
    color: var(--accent-soft, var(--accent));
  }
  .star {
    cursor: pointer;
  }
  .star:focus {
    outline: none;
  }
  text {
    fill: var(--text-dim);
    font-size: 22px;
  }
  /* names get a dark letter outline so they read on nebula clouds */
  .label,
  .eta,
  .count {
    paint-order: stroke;
    stroke: rgba(2, 4, 9, 0.85);
    stroke-width: 2.6px;
    stroke-linejoin: round;
  }
  .label {
    fill: var(--text);
    text-shadow: 0 0 6px var(--bg);
  }
  .dimlabel {
    fill: #b9c4d6;
  }
  .unknown {
    fill: var(--line-bright);
    font-size: 20px;
    pointer-events: none;
  }
  .fog {
    fill: none;
    stroke: var(--line);
    stroke-width: 1.5;
    stroke-dasharray: 3 5;
  }
  .selring {
    fill: none;
    stroke: var(--accent-soft);
    stroke-width: 3;
    animation: selpulse 1.6s ease-in-out infinite;
  }
  @keyframes selpulse {
    0%, 100% { stroke-opacity: 1; r: 36; }
    50% { stroke-opacity: 0.5; r: 40; }
  }
  .target {
    fill: color-mix(in srgb, var(--good) 8%, transparent);
    stroke: var(--good);
    stroke-width: 1.5;
    stroke-dasharray: 6 6;
  }
  .norange {
    fill: none;
    stroke: #5a3030;
    stroke-width: 2;
    stroke-dasharray: 4 6;
  }
  .territory {
    /* group-level opacity: overlapping circles composite to ONE flat region */
    opacity: 0.09;
    pointer-events: none;
  }
  .route {
    stroke: var(--accent);
    stroke-width: 2;
    stroke-dasharray: 10 10;
    opacity: 0.5;
    pointer-events: none;
    animation: routeflow 1.2s linear infinite;
  }
  @keyframes routeflow {
    to { stroke-dashoffset: -20; }
  }
  .fleetmark {
    fill: var(--accent-soft);
    stroke: var(--bg);
    stroke-width: 1.5;
    filter: drop-shadow(0 0 6px color-mix(in srgb, var(--accent) 80%, transparent));
  }
  .fleetmark.reroutable {
    fill: var(--gold);
  }
  .eta {
    font-size: 17px;
    fill: var(--accent-soft);
    pointer-events: none;
  }
  aside {
    width: 22rem;
    max-height: 100%;
    overflow: auto;
    font-size: 0.9rem;
    background: linear-gradient(180deg, var(--panel-2), var(--panel));
    border: 1px solid var(--line);
    border-radius: 10px;
    padding: 0.6rem 0.9rem;
    align-self: flex-start;
  }
  aside h3 {
    margin: 0.2rem 0 0.5rem;
    color: var(--accent-soft);
  }
  aside h3 .ghost {
    opacity: 0.35;
    border: none;
    background: transparent;
    padding: 0 0.2rem;
    font-size: 0.8rem;
  }
  aside h3 .ghost:hover {
    opacity: 1;
  }
  .renamestar {
    width: 11rem;
  }
  .dim,
  .dimtext {
    opacity: 0.65;
  }
  .warn {
    color: var(--gold);
  }
  .go {
    color: var(--good);
  }
  ul {
    padding-left: 1rem;
    list-style: none;
    margin: 0.3rem 0;
  }
  li {
    margin-bottom: 0.3rem;
  }
  .terrainbox {
    display: inline-block;
    vertical-align: top;
    margin-left: 0.4rem;
    font-size: 0.72rem;
  }
  .terrainbox summary {
    cursor: pointer;
    color: var(--text-dim);
  }
  .terrainbox summary:hover {
    color: var(--text);
  }
  .terrainrow {
    display: flex;
    align-items: flex-start;
    gap: 0.4rem;
    margin-top: 0.2rem;
  }
  .minimap {
    width: 108px;
    border: 1px solid var(--line);
    border-radius: 3px;
    filter: brightness(0.75);
  }
  .doctrine select {
    font-size: 0.7rem;
    padding: 0.1rem 0.25rem;
  }
  .orbit {
    display: inline-block;
    width: 1.2rem;
    height: 1.2rem;
    line-height: 1.2rem;
    text-align: center;
    background: var(--panel-3);
    border-radius: 50%;
    font-size: 0.75rem;
    margin-right: 0.3rem;
    color: var(--accent-soft);
  }
  .monster {
    fill: var(--bad);
    color: var(--bad);
    font-size: 24px;
  }
  .raid {
    fill: var(--gold);
    color: var(--gold);
    font-size: 26px;
    font-weight: 700;
  }
  .blockade {
    fill: var(--gold);
    color: var(--gold);
    font-size: 24px;
  }
  aside .monster,
  aside .raid,
  aside .blockade {
    font-size: 0.9rem;
  }
  .legend {
    flex: 0 0 auto;
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    font-size: 0.78rem;
    color: var(--text-dim);
    padding: 0.4rem 0.2rem 0;
    align-items: center;
  }
  .legend .monster,
  .legend .raid {
    font-size: 0.85rem;
  }
  .legend .sw {
    display: inline-block;
    width: 0.8rem;
    height: 0.8rem;
    border: 2px dashed #5a3030;
    border-radius: 50%;
    vertical-align: middle;
  }
  .legend label {
    display: flex;
    gap: 0.3rem;
    align-items: center;
    color: var(--text);
  }

  @media (min-height: 980px) {
    .wrap {
      height: calc(100dvh - 13rem);
    }
  }
  .wormhole {
    stroke: #b78bff;
    stroke-width: 2;
    stroke-dasharray: 3 9;
    opacity: 0.55;
    pointer-events: stroke;
  }
  .count {
    font-size: 15px;
    font-weight: 700;
  }
  .system {
    width: 100%;
    background: var(--bg);
    border: 1px solid var(--line);
    border-radius: 8px;
    margin: 0.3rem 0 0.1rem;
  }
  .syskey {
    font-size: 0.68rem;
    color: var(--text-dim);
    margin: 0.15rem 0 0.4rem;
  }
</style>
